/* eslint-disable max-lines -- don't want to refactor this legacy component */
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  inject,
  Injector,
  Input,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { faFilter } from '@fortawesome/free-solid-svg-icons';
import { Store } from '@sama/app-state';
import { isDefined } from '@sama/js-core';
import { every, get, size } from '@sama/micro-dash';
import { DirectiveSuperclass } from '@sama/ng-core';
import { mapAndCacheArrayElements } from '@sama/rxjs-core';
import {
  BehaviorSubject,
  combineLatest,
  delay,
  Observable,
  shareReplay,
} from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  take,
  takeWhile,
  withLatestFrom,
} from 'rxjs/operators';
import { NestedOutputFilter } from '../../../../../../../core/h3ml/state/fusion-state';
import {
  OutputInfo,
  outputInfoStoreToken,
} from '../../../../../../../core/h3ml/state/output-info';
import { TaskWorkspaceStore } from '../../../../../../../core/h3ml/state/task-workspace-store';
import { AnnotationLayoutService } from '../../../../../../../core/outputs/annotation/annotation-shared/layout/annotation-layout.service';
import { OutputKeyboardShortcutService } from '../../../../../../../core/outputs/annotation/annotation-shared/utils/output-keyboard-shortcut.service';
import { ShapeSelectionService } from '../../../../../../../core/outputs/annotation/annotation-shared/utils/shape-selection.service';
import { ShapeListContextMenuService } from '../../../../../../../core/shared/context-menu/shape-list-context-menu.service';
import { ShapeFeedbackDialogService } from '../../../../../../../core/shared/feedback/shape-feedback-dialog.service';
import { KeyboardShortcutEventInfo } from '../../../../../../../core/shared/keyboard-shortcuts/root-keyboard-shortcut.service';
import { ShapeState } from '../../../../../../../cross-team-shared/annotation/shared/state/shape-state';
import {
  expandShapeGroups,
  setCssProperty,
} from '../../../../../../../cross-team-shared/misc-utils';
import { keys } from '../../../../../../../cross-team-shared/misc-utils/keys';
import { EventTrackingService } from '../../../../../../../shared/event-tracking/event-tracking.service';
import { EventTrackingValue } from '../../../../../../../team-apis/core/event-tracking';
import { outputAnnotationInfoStoreToken } from '../../../../annotation-providers';
import { AbstractAnnotationStore } from '../../../../state/root/abstract-annotation-store';
import { getNextSelectedObject } from '../../../../utils/list-utils';
import { ShapeFilterService } from '../../shape-filter/shape-filter.service';
import { ShapeListPanelService } from './shape-list-panel.service';

export type ListItem = ShapeListItem | GroupListItem;

interface ShapeListItem {
  readonly type: 'shape';
  index: number;
}

interface GroupListItem {
  readonly type: 'group';
  index: number;
}

@Component({
  selector: 'h3-objectless-shape-list',
  templateUrl: './objectless-shape-list.component.html',
  styleUrls: ['./objectless-shape-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
/** @deprecated used for old projects without objects */
export class ObjectlessShapeListComponent
  extends DirectiveSuperclass
  implements AfterViewInit
{
  @Input() hoverButtonTemplate?: TemplateRef<unknown>;
  @Input() staticButtonTemplate?: TemplateRef<unknown>;

  @ViewChild(CdkVirtualScrollViewport)
  private virtualScrollViewport?: CdkVirtualScrollViewport;
  @ViewChild('shapeList')
  private shapeList?: ElementRef;

  private readonly outputAnnotationInfoStore = inject(
    outputAnnotationInfoStoreToken,
  );
  private readonly shapesStore = this.outputAnnotationInfoStore('shapes');

  faFilter = faFilter;
  filterButtonDisabled$ = this.filterService
    .hasFilters$()
    .pipe(map((val) => !val));
  listItems$: Observable<ListItem[]>;
  groupTypes: string[];
  readOnly$ = this.taskWorkspaceStore('readOnly').$.pipe(map(Boolean));
  shapeCountText$: Observable<string>;
  maxFrameNumber$ = this.taskWorkspaceStore.getMaxFrameNumber$(
    this.outputInfoStore.state().output.name,
  );
  isShapeFilterActive$: Observable<boolean> =
    this.filterService.hasActiveFilters$();
  expandedAll$ = new BehaviorSubject(false);
  isGroupShapesButtonDisplayed$!: Observable<boolean>;
  selectedShapeCount$: Observable<number> =
    this.shapeSelectionService.getSelectedShapeCount$();
  isAddFeedbackButtonDisplayed = this.shapeFeedbackDialogService.isEditable();
  isAddFeedbackButtonDisabled$ = this.selectedShapeCount$.pipe(
    map((count) => count !== 1),
  );
  useVirtualScrolling$!: Observable<boolean>;
  useRegularScrolling$!: Observable<boolean>;
  skipNextSelectionEvent = false;

  private isCurrentOutput$ = this.taskWorkspaceStore(
    'selectedOutputName',
  ).$.pipe(
    map(
      (selectedOutputName) =>
        selectedOutputName === this.outputInfoStore.state().output.name,
    ),
    filter((isCurrentOutput) => isCurrentOutput),
  );
  private eventTrackingService = inject(EventTrackingService);

  constructor(
    public store: AbstractAnnotationStore,
    private keyboardShortcutService: OutputKeyboardShortcutService,
    private shapeSelectionService: ShapeSelectionService,
    @Inject(outputInfoStoreToken)
    private outputInfoStore: Store<OutputInfo>,
    private filterService: ShapeFilterService,
    private shapeListPanelService: ShapeListPanelService,
    private shapeFeedbackDialogService: ShapeFeedbackDialogService,
    private taskWorkspaceStore: TaskWorkspaceStore,
    private contextMenuService: ShapeListContextMenuService,
    private annotationLayoutService: AnnotationLayoutService,
    injector: Injector,
  ) {
    super(injector);

    this.groupTypes = this.getGroupTypes();
    this.isGroupShapesButtonDisplayed$ = this.readOnly$.pipe(
      map((readOnly) => !readOnly && !!this.groupTypes.length),
    );
    this.listItems$ = this.mapOrderItemIndexesToStores$();
    this.shapeCountText$ = this.buildShapeCountText$();
    this.initializeScrollingType();
    this.setSubscriptionToSelectShapeKeyboardEvents();
  }

  ngAfterViewInit() {
    this.setUpScrollIntoViewSubscription();
    this.setUpScrollbarWidthSubscription();
  }

  dragStarted(item: ListItem) {
    if (item.type === 'group') {
      this.shapeListPanelService.setGroupPanelState(item.index, false);
    } else {
      this.shapeListPanelService.setShapePanelState(item.index, false);
    }
  }

  showFilterEditor() {
    this.filterService.showFilterEditor();
  }

  reorder(_event: CdkDragDrop<ListItem, ListItem, ListItem>) {}

  expandAll() {
    this.shapeListPanelService.setExpandedStateForAllShapeListPanels(true);
    this.expandedAll$.next(true);
  }

  collapseAll() {
    this.shapeListPanelService.setExpandedStateForAllShapeListPanels(false);
    this.expandedAll$.next(false);
  }

  openFeedbackDialog() {
    this.shapeFeedbackDialogService.openAddShapeFeedbackDialog();
  }

  openContextMenu($event: MouseEvent, item: ListItem) {
    $event.preventDefault();

    return this.contextMenuService.open(
      [$event.clientX, $event.clientY],
      item.index,
    );
  }

  private getGroupTypes() {
    const groupTypesFromTheServer = get(this.outputInfoStore.state(), [
      'output',
      'meta',
      'group_types',
    ]);

    return groupTypesFromTheServer || [];
  }

  /** @deprecated used for old objectless projects */
  private mapOrderItemIndexesToStores$() {
    return combineLatest([
      this.outputAnnotationInfoStore('shapeAndGroupOrder').$.pipe(
        filter(isDefined),
      ),
      this.shapesStore.$,
      this.filterService.getNestedOutputShapeFilters$(),
    ]).pipe(
      map(([orderItems, shapes, nestedOutputShapeFilters]) =>
        [...orderItems].reverse().filter((i) =>
          // @ts-ignore -- fix me
          this.applyFilters(shapes[i.index], nestedOutputShapeFilters),
        ),
      ),
      mapAndCacheArrayElements(
        (orderItem) => `${orderItem.type}-${orderItem.index}`,
        (item) => ({
          type: item.type,
          index: item.index,
        }),
      ),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  /** @deprecated used for old objectless projects */
  private applyFilters(
    shape: ShapeState,
    nestedOutputFilters: StrictRecord<string, NestedOutputFilter>,
  ) {
    // fix me if you change this code
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
    if (!shape || !shape.tags) {
      return true;
    }
    return every(nestedOutputFilters, (nestedOutputFilter) => {
      const answerOptionName =
        // @ts-ignore -- fix me
        shape.tags[nestedOutputFilter.outputName] || 'none';
      // @ts-ignore -- fix me
      return !nestedOutputFilter.namesAndLabels.some(
        (nameAndLabel) =>
          nameAndLabel.name === answerOptionName && nameAndLabel.filterOut,
      );
    });
  }

  private buildShapeCountText$() {
    const selectedShapeCount$ =
      this.shapeSelectionService.getSelectedShapeCount$();
    const shapeCount$ = this.shapesStore.$.pipe(map(size));
    return combineLatest([selectedShapeCount$, shapeCount$]).pipe(
      map(
        ([selectionCount, shapeCount]) =>
          `Shapes: ${selectionCount}/${shapeCount}`,
      ),
    );
  }

  /*
   * When virtual scrolling is enabled, not all shapes are rendered so we can't
   * rely on them to scroll themselves into view.
   */
  private setUpScrollIntoViewSubscription() {
    const revealShape$ = combineLatest([
      this.shapeSelectionService.getSelectionForOutput$(),
      this.isCurrentOutput$,
      this.useVirtualScrolling$,
    ]).pipe(
      filter(
        // @ts-ignore -- fix me
        ([currentSelection, isCurrentOutput, useVirtualScrolling]) =>
          // @ts-ignore -- fix me
          size(currentSelection) === 1 &&
          isCurrentOutput &&
          useVirtualScrolling,
      ),
      // @ts-ignore -- fix me
      map(([selection]) => keys(selection)[0]),
      // adding a delay of 0 allows the browser to have time to switch tab
      delay(0),
    );

    this.subscribeTo(
      revealShape$.pipe(withLatestFrom(this.listItems$)),
      ([shapeIndex, listItems]) => {
        this.scrollToShape(listItems, shapeIndex);
      },
    );
  }

  private scrollToShape(listItems: ListItem[], shapeIndex: number) {
    // Since we can't use scrollIntoView with virtual scrolling, we have to use
    // the virtual scroll viewport. However, unlike scrollIntoView which does
    // nothing when the element is already into view, the virtual scroll
    // viewport always scroll regardless of if the item is already in view. To
    // mitigate that, we skip the action when the scrolling is triggered from
    // selecting from the shape list, where we are certain we don't want to
    // scroll.
    if (this.skipNextSelectionEvent) {
      this.skipNextSelectionEvent = false;
      return;
    }

    const listIndex = listItems.findIndex((item) => {
      if (item.type === 'shape') {
        return item.index === shapeIndex;
      } else {
        const group = this.outputAnnotationInfoStore.state().groups[item.index];
        // @ts-ignore -- fix me
        return group.shapeIndexes.includes(shapeIndex);
      }
    });
    this.virtualScrollViewport!.scrollToIndex(listIndex);
  }

  private setUpScrollbarWidthSubscription() {
    this.subscribeTo(
      combineLatest([this.shapesStore.$, this.useVirtualScrolling$]).pipe(
        // 0 is macroqueue, leaves time to the item to render and the scrollbar
        // to appear
        debounceTime(0),
        // once the scrollbar width is set by one of the outputs, it's the same
        // everywhere, so we don't need this subscription anymore
        takeWhile(
          // eslint-disable-next-line rxjs/no-ignored-takewhile-value -- we don't care about the range
          () => !document.body.style.getPropertyValue('--ursa-scrollbar-width'),
        ),
        filter(
          ([shapes]) =>
            !!(this.virtualScrollViewport ?? this.shapeList) &&
            size(shapes) > 0,
        ),
        map(() => {
          const elementRef = (this.virtualScrollViewport?.getElementRef() ??
            this.shapeList)!;
          const scrollContainer = elementRef.nativeElement;
          return scrollContainer.offsetWidth - scrollContainer.clientWidth;
        }),
        filter((width) => width > 0),
        take(1),
      ),
      (width) => {
        setCssProperty(document.body, '--ursa-scrollbar-width', `${width}px`);
      },
    );
  }

  private initializeScrollingType() {
    this.useRegularScrolling$ = this.listItems$.pipe(
      map((items) => items.length <= 300),
      distinctUntilChanged(),
    );

    this.useVirtualScrolling$ = combineLatest([
      this.useRegularScrolling$,
      // we must not render the virtual scroll viewport while it's hidden or
      // else the available height will be wrong
      this.annotationLayoutService.isRightPanelHidden$,
    ]).pipe(
      map(
        ([useRegularScrolling, isRightPanelHidden]) =>
          !useRegularScrolling && !isRightPanelHidden,
      ),
      distinctUntilChanged(),
    );
  }

  private setSubscriptionToSelectShapeKeyboardEvents() {
    this.subscribeTo(
      this.keyboardShortcutService.getEvent$('selectNextAnnotation'),
      async (keyboardEventInfo) => {
        // use 'backward' to select the next shape because the list is
        // reversed
        this.handleSelectShapeKeyboardEvent(keyboardEventInfo, 'backward');
      },
    );

    this.subscribeTo(
      this.keyboardShortcutService.getEvent$('selectPreviousAnnotation'),
      async (keyboardEventInfo) => {
        // use 'forward' to select the previous shape because the list is
        // reversed
        this.handleSelectShapeKeyboardEvent(keyboardEventInfo, 'forward');
      },
    );
  }

  private handleSelectShapeKeyboardEvent(
    { keyboardEvent, eventTrackingValue }: KeyboardShortcutEventInfo,
    direction: 'forward' | 'backward',
  ) {
    keyboardEvent.preventDefault();

    const selectedShapeIndexes =
      this.shapeSelectionService.getSelectedShapeIndexes();

    if (selectedShapeIndexes.length !== 1) {
      return;
    }

    const selectedIndex = selectedShapeIndexes[0];
    const { shapeAndGroupOrder, groups } =
      this.outputAnnotationInfoStore.state();
    // @ts-ignore -- fix me
    const flattenedShapes = expandShapeGroups(shapeAndGroupOrder, groups);
    const nextIndex = getNextSelectedObject(
      flattenedShapes.map((item) => item.index),
      selectedIndex,
      direction,
    );
    this.shapeSelectionService.setSelection([nextIndex]);
    this.trackSelectionChangeEvent({
      ...eventTrackingValue,
      shapeIndex: nextIndex,
    });
  }

  private trackSelectionChangeEvent(
    eventTrackingValue: EventTrackingValue & {
      shapeIndex: number;
    },
  ) {
    const { shapeIndex, ...rest } = eventTrackingValue;
    const shapeInfo = this.shapesStore.state()[shapeIndex];

    if (shapeInfo) {
      this.eventTrackingService.track({
        id: 'change_shape_selection',
        bucket: 'adjustment',
        method: 'set',
        source: 'keyboard_shortcut',
        ...rest,
        resulting_shape_selection: [
          { index: shapeInfo.index, type: shapeInfo.type },
        ],
        resulting_object_selection: [],
      });
    }
  }
}
